import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function loadUser() {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const res = await api.get('/auth/me');
          setUser(res.data);
        } catch {
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setReady(true);
    }
    loadUser();
  }, []);

  function login(userData, jwt) {
    localStorage.setItem('token', jwt);
    setToken(jwt);
    setUser(userData);
  }

  // Re-fetch the authoritative user from /auth/me and update context state.
  // Returns the fresh user so callers can read updated fields without waiting
  // for a re-render. Callers own error handling (does not catch internally).
  async function refreshUser() {
    const res = await api.get('/auth/me');
    setUser(res.data);
    return res.data;
  }

  function logout() {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    navigate('/login');
  }

  // Derived payment-state flag, exposed for a later gating step.
  // NOTE: nothing consumes this yet — it does not gate routes or change any UI.
  const hasAccess = (user?.access_tier === 'beta') || (user?.subscription_status === 'active');

  if (!ready) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: 'var(--paper)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          backgroundColor: 'var(--card)',
          boxShadow: 'var(--sh-card)',
          borderRadius: 'var(--r-card)',
          padding: '32px 48px',
          textAlign: 'center',
        }}>
          <span style={{
            fontFamily: 'var(--font-serif)',
            fontSize: '1.5rem',
            fontWeight: 700,
            letterSpacing: '-0.3px',
          }}>
            <span style={{ color: 'var(--ink)' }}>Tru</span>
            <span style={{ color: 'var(--signal)' }}>Flow</span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, ready, hasAccess, refreshUser, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}